import React, { useRef, useState, useEffect, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Spinner, Icon } from '@iq/react-components';
import classnames from 'classnames';

import { getActiveSite } from '../../../../bundles/sites';
import { useSubscription, getSingleVisualization } from '../../../../bundles/visualizations';
import { getTimezone } from '../../../../bundles/application';
import { getSiteVariablesIndex } from '../../../../bundles/sources';
import { useClientSize } from '../../../../utils';
import { DropdownMenu } from '../../../DropdownMenu';

import Loader from '../../../Loader';
import {
  getCurrentPentagonArea,
  getGasCentroid,
  getPentagonAreas,
  normalizeCoordinate,
  tooltipLabelFormatter,
} from './helpers';
import {
  PentagonSummitLabels,
  PentagonLegend,
  PentagonOutline,
  PentagonAreaLines,
  PentagonAreaHighlights,
  PointToolTip,
  RollingAveragePlot,
  DrawPentagonArea,
} from './components';
import { useDuvalPentagonData } from './hooks/useDuvalPentagonData';
import { useDuvalPentagonSeries } from './hooks/useDuvalPentagonSeries';
import { usePentagonRolling } from './hooks/usePentagonRolling';

/**
 * Duval Pentagon: diagnoses transformer faults from dissolved gas analysis (DGA).
 * Plots the relative proportions of 5 dissolved gases (H2, C2H6, CH4, C2H4, C2H2)
 * as a centroid inside a pentagon, and the colored zone the centroid lands in
 * indicates the likely fault type.
 *
 * Supports Pentagon types 1 and 2, which use the same gases but different zones:
 *   - Type 1: PD, S, T1, T2, T3, D1, D2
 *   - Type 2: PD, S, O, C, T3-H, D1, D2
 */

function AreaToolTip({ position, text }) {
  return (
    <div
      className="tooltip"
      style={{ left: position.x, top: position.y }}
    >
      {text}
    </div>
  );
}

const DuvalPentagonVisualization = memo(
  ({
    visualization,
    panelId,
    pollingDateRange,
    onExportData,
    onFullscreen,
    isPreview,
    isFullscreen,
    selected,
    onSelect,
    openSignalViewer,
    CustomTooltip,
    getMoreOptions,
    dropdownPlacement,
  }) => {
    const dispatch = useDispatch();
    const site = useSelector(getActiveSite);
    const timezone = useSelector(getTimezone);
    const variablesIndex = useSelector(getSiteVariablesIndex);

    const [legendHeight, setLegendHeight] = useState(0);

    const [errors, setErrors] = useState([]);

    const [isAreaHighlighted, setIsAreaHighlighted] = useState({});

    const [areaTooltip, setAreaTooltip] = useState({
      show: false,
      position: { x: 0, y: 0 },
      text: '',
    });

    const [pointTooltip, setPointTooltip] = useState({
      show: false,
      position: { x: 0, y: 0 },
      time: 0,
    });

    const {
      initialLoaded,
      seriesValues: values,
      loading,
    } = useSubscription(visualization.id, panelId);
    const { variables = [], configuration = {} } = visualization;
    const [{ height, width }, clientRef] = useClientSize();
    const legendRef = useRef(null);

    const addLegendHeight = configuration.legend.show ? -legendHeight : 0;
    const addIsPreviewHeight = !isPreview ? -53.5 : 0;
    const addErrorHeight = errors.length ? -32 : 0;

    const largestSize = Math.min(width, height);

    const vizSize =
      largestSize && largestSize + addLegendHeight + addIsPreviewHeight + addErrorHeight - 20;

    const pentagonSize = vizSize / (1 / 2) / Math.sqrt(3);
    const pentagonWidth = pentagonSize;
    const pentagonHeight = pentagonSize;

    useEffect(() => {
      if (legendRef.current) {
        setLegendHeight(legendRef.current.clientHeight);
      }
    }, [configuration.legend.show]);

    useEffect(() => {
      if (initialLoaded && !selected) {
        dispatch(getSingleVisualization(visualization, panelId));
      }
    }, [
      panelId,
      initialLoaded,
      JSON.stringify(`${pollingDateRange.startDate}:${pollingDateRange.endDate}`),
    ]);

    const data = useDuvalPentagonData(values, variables, variablesIndex, isPreview);
    const series = useDuvalPentagonSeries(variables, variablesIndex, configuration);
    const rollingAverageData = usePentagonRolling(
      configuration,
      series,
      data,
      pentagonWidth,
      pentagonHeight
    );

    useEffect(() => {
      setIsAreaHighlighted({});
    }, [data]);

    const PointPlot = ({ plot, duvalMode }) => {
      const h2Serie = series.filter((serie) => serie.id === plot.h2Id)[0];
      const c2h6Serie = series.filter((serie) => serie.id === plot.c2h6Id)[0];
      const ch4Serie = series.filter((serie) => serie.id === plot.ch4Id)[0];
      const c2h4Serie = series.filter((serie) => serie.id === plot.c2h4Id)[0];
      const c2h2Serie = series.filter((serie) => serie.id === plot.c2h2Id)[0];

      if (!h2Serie || !c2h6Serie || !ch4Serie || !c2h4Serie || !c2h2Serie) {
        return null;
      }

      const filteredData = data.filter(
        (d) =>
          d[h2Serie.id] && d[c2h6Serie.id] && d[ch4Serie.id] && d[c2h4Serie.id] && d[c2h2Serie.id]
      );
      if (filteredData.length === 0) {
        return null;
      }
      const h2 = h2Serie && filteredData[filteredData.length - 1][h2Serie.id];
      const c2h6 = c2h6Serie && filteredData[filteredData.length - 1][c2h6Serie.id];
      const ch4 = ch4Serie && filteredData[filteredData.length - 1][ch4Serie.id];
      const c2h4 = c2h4Serie && filteredData[filteredData.length - 1][c2h4Serie.id];
      const c2h2 = c2h2Serie && filteredData[filteredData.length - 1][c2h2Serie.id];

      const lastPointArea = getCurrentPentagonArea({
        h2,
        c2h6,
        ch4,
        c2h4,
        c2h2,
        duvalType: plot.duvalType,
        pentagonWidth,
        pentagonHeight,
      });

      useEffect(() => {
        setIsAreaHighlighted((state) => {
          if (state[lastPointArea]) return state;
          return { ...state, [lastPointArea]: true };
        });
      }, [lastPointArea]);

      switch (duvalMode) {
        case 'current':
          return (
            <PentagonPoint
              color={plot.color}
              length={0}
              last={true}
              h2={h2}
              ch4={ch4}
              c2h6={c2h6}
              c2h4={c2h4}
              c2h2={c2h2}
              time={filteredData[filteredData.length - 1].time}
            />
          );
        case 'over time':
          return (
            <OverTimePointPlot
              h2Serie={h2Serie}
              c2h6Serie={c2h6Serie}
              ch4Serie={ch4Serie}
              c2h4Serie={c2h4Serie}
              c2h2Serie={c2h2Serie}
              color={plot.color}
            />
          );
        default:
          return null;
      }
    };

    function PentagonPoint({
      idx = 0,
      h2 = 'Missing',
      c2h6 = 'Missing',
      ch4 = 'Missing',
      c2h4 = 'Missing',
      c2h2 = 'Missing',
      color = 0,
      last = false,
      length = 1,
      time = 0,
    }) {
      if (h2 === 0 && c2h6 === 0 && ch4 === 0 && c2h4 === 0 && c2h2 === 0) {
        if (!errors.length) {
          setErrors((state) => [
            ...state,
            `All recieved values are 0. - ${tooltipLabelFormatter(time, timezone)}`,
          ]);
        }
      }
      if (h2 < 0 || c2h6 < 0 || ch4 < 0 || c2h4 < 0 || c2h2 < 0) {
        if (!errors.length) {
          setErrors((state) => [
            ...state,
            `Negative value recieved. - ${tooltipLabelFormatter(time, timezone)}`,
          ]);
        }
      }

      const gasses = [h2, c2h6, ch4, c2h4, c2h2];

      const centroidCoordinates = getGasCentroid(gasses);

      return (
        <>
          <circle
            r={last ? configuration.pointRadius + 2 : configuration.pointRadius}
            cx={`${50 + normalizeCoordinate(centroidCoordinates.x)}%`}
            cy={`${50 + normalizeCoordinate(centroidCoordinates.y)}%`}
            className="duval-point"
            strokeWidth={2}
            stroke={
              (time === pointTooltip.time && pointTooltip.show && 'black') ||
              (last && 'white') ||
              ''
            }
            style={{ opacity: `${(100 / length) * (idx + 1)}%` }}
            fill={color}
            onMouseOut={() => {
              setPointTooltip((prev) => ({ ...prev, show: false }));
            }}
            onMouseOver={(e) => {
              if (pointTooltip.show || areaTooltip.show) {
                return;
              }
              setPointTooltip((prev) => ({
                ...prev,
                show: true,
                time,
                position: { x: e.clientX + 10, y: e.clientY + 10 },
              }));
            }}
          />
        </>
      );
    }

    const OverTimePointPlot = ({
      h2Serie = null,
      c2h6Serie = null,
      ch4Serie = null,
      c2h4Serie = null,
      c2h2Serie = null,
      color,
    }) => {
      // Filtering out missing datapoints which cannot be plotted on the duval
      const filteredData = data.filter(
        (d) =>
          d[h2Serie.id] && d[c2h6Serie.id] && d[ch4Serie.id] && d[c2h4Serie.id] && d[c2h2Serie.id]
      );
      return (
        h2Serie &&
        c2h6Serie &&
        ch4Serie &&
        c2h4Serie &&
        c2h2Serie &&
        filteredData.map((point, idx) => {
          const h2 = point[h2Serie.id];
          const c2h6 = point[c2h6Serie.id];
          const ch4 = point[ch4Serie.id];
          const c2h4 = point[c2h4Serie.id];
          const c2h2 = point[c2h2Serie.id];

          return (
            <PentagonPoint
              color={color}
              length={filteredData.length}
              idx={idx}
              key={idx + h2Serie.id + c2h6Serie.id + ch4Serie.id}
              last={idx === filteredData.length - 1}
              h2={h2}
              ch4={ch4}
              c2h6={c2h6}
              c2h4={c2h4}
              c2h2={c2h2}
              time={point.time}
            />
          );
        })
      );
    };

    const PentagonAreas = () =>
      getPentagonAreas(configuration.duvalType).map((area) => (
        <DrawPentagonArea
          key={area.name}
          area={area}
          transparent={false}
          pentagonWidth={pentagonWidth}
          pentagonHeight={pentagonHeight}
          areaTooltip={areaTooltip}
          setAreaTooltip={setAreaTooltip}
          pointTooltip={pointTooltip}
        />
      ));

    const Errors = () => (
      <div style={{ textAlign: 'center', maxWidth: width }}>Signal Error: {errors[0]}</div>
    );

    const handleDownloadClick = () => {
      onExportData(data[0].granularity);
    };
    const handleSelect = () => {
      onSelect(visualization);
    };
    const classes = classnames('menu', {});

    const vizHeight = height - 28; // less header
    const loaderHeight = vizHeight - 28; // less loader padding

    if (series.length < 5) {
      return (
        <div className="duval-error-container">
          At least 5 Signals are required to create a duval pentagon plot.
        </div>
      );
    }

    return (
      <div
        className={`duval-visualization-component ${selected ? 'checked' : ''}`}
        ref={clientRef}
      >
        {!isPreview && !isFullscreen ? (
          <div
            className="duval-header"
            onDoubleClick={(e) => {
              e.stopPropagation();
            }}
          >
            <div className="header-title truncateText">
              <div
                className={`select ${selected ? 'checked' : ''}`}
                onClick={handleSelect}
              >
                <Icon
                  icon={`${selected ? 'he-checkbox-selected' : 'he-checkbox'}`}
                  size="s"
                />
              </div>
              <CustomTooltip content={configuration?.title || visualization?.name}>
                <span className="title truncateText">
                  {configuration?.title || visualization?.name || '\u00A0'}
                </span>
              </CustomTooltip>
            </div>

            <div className={classes}>
              <DropdownMenu
                trigger="he-moreoptionsvertical"
                triggerSize="s"
                menuOptions={getMoreOptions({
                  loading,
                  site,
                  isPreview,
                  onFullscreen,
                  data,
                  handleDownloadClick,
                  openSignalViewer,
                })}
                menuXPlacement={dropdownPlacement?.x}
                menuYPlacement={dropdownPlacement?.y}
                styles={{
                  dropdownIndicator: { padding: '0px' },
                  option: { cursor: 'pointer' },
                }}
              />
            </div>

            {initialLoaded && loading && (
              <Spinner
                size="s"
                className="spinner"
              />
            )}
          </div>
        ) : (
          !isFullscreen && (
            <div className="duval-preview-title">
              <span className="title">{configuration.title || visualization.name || '\u00A0'}</span>
            </div>
          )
        )}
        {!initialLoaded && loading && data.length === 0 ? (
          <Loader
            text="Loading ..."
            height={loaderHeight}
          />
        ) : (
          <div className="duval-wrapper">
            {areaTooltip.show && (
              <AreaToolTip
                position={areaTooltip.position}
                text={areaTooltip.text}
              />
            )}

            {pointTooltip.show && (
              <PointToolTip
                pointTooltip={pointTooltip}
                setPointTooltip={setPointTooltip}
                timezone={timezone}
                data={data}
                series={series}
                plots={configuration.plots}
                pentagonWidth={pentagonWidth}
                pentagonHeight={pentagonHeight}
              />
            )}

            <div width="100%">
              <div
                className="duval-container"
                onMouseOut={() => {
                  setAreaTooltip((prev) => ({ ...prev, show: false }));
                  setPointTooltip((prev) => ({ ...prev, show: false }));
                }}
              >
                <svg
                  id={`duval${visualization.id}`}
                  className="duval-surface"
                  width={pentagonWidth}
                  height={pentagonHeight}
                >
                  <PentagonAreas />

                  <PentagonAreaLines duvalType={configuration.duvalType} />

                  {configuration.highlightAreas?.show && (
                    <PentagonAreaHighlights
                      duvalType={configuration.duvalType}
                      isAreaHighlighted={isAreaHighlighted}
                      pentagonWidth={pentagonWidth}
                      pentagonHeight={pentagonHeight}
                    />
                  )}

                  <PentagonOutline />

                  <PentagonSummitLabels />

                  {configuration.plots &&
                    configuration.plots.map((plot, idx) => (
                      <PointPlot
                        key={idx}
                        plot={plot}
                        duvalMode={configuration.duvalMode ? configuration.duvalMode : ''}
                      />
                    ))}

                  {configuration.rollingAverage?.show && (
                    <g>
                      {rollingAverageData?.map((rollingPlot, idx) => (
                        <RollingAveragePlot
                          key={idx}
                          rollingPlot={rollingPlot}
                          configuration={configuration}
                        />
                      ))}
                    </g>
                  )}
                </svg>
                {errors.length ? <Errors /> : null}
                {configuration.legend && configuration.legend.show && (
                  <PentagonLegend
                    plots={configuration.plots}
                    rollingAverage={configuration.rollingAverage}
                    legendRef={legendRef}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
  (prevProps, props) => JSON.stringify(prevProps) === JSON.stringify(props)
);

export default DuvalPentagonVisualization;
